import { clsx } from 'clsx';
import { forwardRef } from 'react';

export interface CardProps {
  children: React.ReactNode;
  className?: string;
  hover?: boolean;
  /** Accessible role - defaults to 'article' for content cards, can be 'region' or custom */
  role?: string;
  /** ARIA label for the card (required if no heading inside) */
  'aria-label'?: string;
  /** ID of the element that labels this card */
  'aria-labelledby'?: string;
  /** Makes card clickable with proper keyboard support */
  onClick?: () => void;
  /** Href for card-as-link pattern */
  href?: string;
}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ 
    children, 
    className, 
    hover = false, 
    role = 'article',
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    onClick,
    href,
  }, ref) => {
    const isClickable = onClick || href;
    
    const baseClasses = clsx(
      'bg-slate-900 border border-slate-800 rounded-xl overflow-hidden',
      hover && 'transition-all duration-300 ease-out hover:border-blue-500/30 hover:shadow-xl hover:shadow-blue-900/20 hover:-translate-y-1',
      isClickable && 'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950',
      className
    );

    // If it's a link card, render as anchor
    if (href) {
      return (
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          href={href}
          className={baseClasses}
          role={role}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
        >
          {children}
        </a>
      );
    }

    // If clickable, add keyboard handlers
    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        onClick?.();
      }
    };

    return (
      <div
        ref={ref}
        className={baseClasses}
        role={role}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        onClick={onClick}
        onKeyDown={isClickable ? handleKeyDown : undefined}
        tabIndex={isClickable ? 0 : undefined}
      >
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';

interface CardHeaderProps {
  children: React.ReactNode; 
  className?: string;
}

export function CardHeader({ children, className }: CardHeaderProps) {
  return (
    <div className={clsx('px-6 py-4 border-b border-slate-800', className)}>
      {children}
    </div>
  );
}

interface CardTitleProps {
  children: React.ReactNode;
  className?: string;
  /** HTML heading level - defaults to h3 */
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

export function CardTitle({ children, className, as: Component = 'h3' }: CardTitleProps) {
  return (
    <Component className={clsx('text-xl font-semibold text-white', className)}>
      {children}
    </Component>
  );
}

interface CardDescriptionProps {
  children: React.ReactNode;
  className?: string;
}

export function CardDescription({ children, className }: CardDescriptionProps) {
  return (
    <p className={clsx('text-slate-400 text-sm mt-1', className)}>
      {children}
    </p>
  );
}

interface CardContentProps {
  children: React.ReactNode;
  className?: string;
}

export function CardContent({ children, className }: CardContentProps) {
  return (
    <div className={clsx('p-6', className)}>
      {children}
    </div>
  );
}

interface CardFooterProps {
  children: React.ReactNode;
  className?: string;
}

export function CardFooter({ children, className }: CardFooterProps) {
  return (
    <div className={clsx('px-6 py-4 border-t border-slate-800 bg-slate-900/50', className)}>
      {children}
    </div>
  );
}

interface CardImageProps {
  src: string;
  alt: string;
  className?: string;
}

export function CardImage({ src, alt, className }: CardImageProps) {
  return (
    <img
      src={src}
      alt={alt}
      className={clsx('w-full h-48 object-cover', className)}
      loading="lazy"
    />
  );
}
